import type { CalcPolicy } from "./types";

/**
 * Политика расчёта по умолчанию.
 *
 * Правила себестоимости «доля от цены» здесь нет — его нет и в практике:
 * разбор архива показал, что расход вводится руками там, где работу делает
 * не наш человек, а внутренние часы идут с нулём. Наценки на внешних —
 * медианы того же архива, до цифры от Braining.
 *
 * План рентабельности ищется по подотделу (Brands, Digital, Media), а если
 * его нет — по типу проекта. Так это устроено у Braining: «Comm делится на
 * brands, digital и media, и у них разная рента», а внутри подотдела «бывает
 * разная целевая рентабельность на уровне блока услуг» — под это у блока есть
 * своя цель.
 *
 * Здесь оба справочника пусты, а `planMarginDefault` — общий ориентир 50%,
 * названный заказчиком 25 августа: «ставь дефолт 50%, пусть поменяют, когда
 * дойдёт дело до дела». Это честнее, чем рассадить одну и ту же заглушку по
 * всем строкам и сделать вид, что они разные. Настоящие числа агентство
 * вносит на экране «Блоки работ», они лежат в базе (`0011_project_plans.sql`)
 * и приходят сюда через стор.
 */
export const DEFAULT_POLICY: CalcPolicy = {
  clientRoundStep: 100,
  blockTolerance: 0.05,
  planMargin: { bySubdivision: {}, byType: {} },
  planMarginDefault: 0.5,
  contractorMarkup: {
    default: 0.82,
    byUnit: { час: 0.54, шт: 1.6, штука: 1.6, комплекс: 0.9, комплект: 0.9 },
  },
};

/** Округление денег до копеек — гасит дрейф двоичной арифметики в суммах. */
export function money(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Округление клиентской суммы до шага (по ТЗ — не мельче 100 ₽).
 *
 * Симметрично относительно нуля: `Math.round` округляет −250 до −200, то есть
 * в сторону нуля для отрицательных и от нуля для положительных. У
 * возвратной комиссии площадки суммы отрицательные, и такая несимметричность
 * давала разные шаги округления для дохода и расхода.
 */
export function roundClient(value: number, step: number): number {
  if (!step) return money(value);
  const sign = value < 0 ? -1 : 1;
  return sign * Math.round(Math.abs(value) / step) * step;
}

/** Доля без деления на ноль: у пустой строки рентабельность считаем нулевой. */
export function share(part: number, whole: number): number {
  if (!whole) return 0;
  return part / whole;
}
